import { saveAs } from 'file-saver'
import {
  //Mutations
  SET_VIS_DATA,
  SET_LF_API_URL,
  SET_VIS_DATA_LOADED,
  SET_VIS_ERROR_OCCURED,
  SET_REPORT_LOADING,
  SET_EXCEL_LOADING,
  SET_PDF_LOADING,
  SET_REPORT_ERROR,
  SET_FILTER_STACK,
  SET_NO_DATA_ERROR,

  // Actions
  UPDATE_FILTER,
  DOWNLOAD_EXCEL_FILE,
  DOWNLOAD_DASHBOARD_PDF,
  ACKNOWLEDGE_ERRORS,

  // Getters
  VIS_DATA_LOADED,
  VIS_ERROR_OCCURED,
  REPORT_LOADING,
  EXCEL_LOADING,
  PDF_LOADING,
  REPORT_ERROR,
  NO_DATA_ERROR,
  NET_RCLM_SINCE_INCP,
  NET_RCLM_YTD,
  NET_RCLM_PROCESSING,
  NET_RCLM_FILED,
  NUM_ACTIVE_ACCTS,
  NUM_ACTIVE_TRANSACTIONS,
  NUM_PAID_ACCTS,
  NUM_PAID_TRANSACTIONS,
  NUM_EVENTS,
  PAID_SINCE_INCP,
  PAID_YTD,
  MIDAS_STATUS_GROUPS,
  PAID_BY_YEAR_GROUPS,
  CLIENT_STATUS_GROUPS,
  COUNTRY_GROUPS,
  FI_ACCT_GROUPS,
} from '../reclaim-dashboard-serverside-types'

// import { getDataGroups, getExcelData } from './lf-api-helper'
import {
  getDataGroups,
  getExcelData,
  GetReclaimDashboardPdf,
} from '@/services/api/LF/lf-api-helper'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})
const commaFormatter = new Intl.NumberFormat('en-US')

const formatCurrency = (n) => currencyFormatter.format(n)

const formatComma = (n) => commaFormatter.format(n)

const state = {
  visDataLoaded: false,
  visErrorOccured: null,
  reportLoading: false,
  excelLoading: false,
  pdfLoading: false,
  reportError: false,
  noDataError: false,
  lfApiUrl: null,
  rdMetaData: {
    id: 0,
    name: 'Reclaims Dashboard', // 'Quarterly Status Report',
    route: '/reports/reclaim-dashboard',
    attachment: null, // 'Sample Quarterly Status Report.xlsx',
    icon: 'chart pie',
    lastUpdated: true,
    description: 'Track and analyze outstanding reclaims',
  },
  summaryStats: null,
  midasStatGroups: null,
  clientFacingStatGroups: null,
  yearGroups: null,
  countryGroups: null,
  fiAcctGroups: null,
}

const getters = {
  [VIS_DATA_LOADED](state) {
    return state.visDataLoaded
  },
  [VIS_ERROR_OCCURED](state) {
    return state.visErrorOccured
  },
  [REPORT_LOADING](state) {
    return state.excelLoading || state.pdfLoading
  },
  [EXCEL_LOADING](state) {
    return state.excelLoading
  },
  [PDF_LOADING](state) {
    return state.pdfLoading
  },
  [REPORT_ERROR](state) {
    return state.reportError
  },
  [NO_DATA_ERROR](state) {
    return state.noDataError
  },

  // Visualization Getters
  [NET_RCLM_SINCE_INCP](state) {
    if (state.summaryStats)
      return formatCurrency(state.summaryStats.paidSinceIncp)
    else return null
  },
  [NET_RCLM_YTD](state) {
    if (state.summaryStats) return formatCurrency(state.summaryStats.paidYTD)
    else return null
  },
  [NET_RCLM_PROCESSING](state) {
    if (state.summaryStats)
      return formatCurrency(state.summaryStats.totalProcessing)
    else return null
  },
  [NET_RCLM_FILED](state) {
    if (state.summaryStats) return formatCurrency(state.summaryStats.totalFiled)
    else return null
  },
  [NUM_ACTIVE_ACCTS](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.numActiveAcct)
    else return null
  },
  [NUM_ACTIVE_TRANSACTIONS](state) {
    if (state.summaryStats)
      return formatComma(state.summaryStats.numActiveTrans)
    else return null
  },
  [NUM_PAID_ACCTS](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.numPaidAcct)
    else return null
  },
  [NUM_PAID_TRANSACTIONS](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.numPaidTrans)
  },
  [NUM_EVENTS](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.numEvents)
    else return null
  },
  [PAID_SINCE_INCP](state) {
    if (state.summaryStats)
      return formatCurrency(state.summaryStats.paidSinceIncp)
    else return null
  },
  [PAID_YTD](state) {
    if (state.summaryStats) return formatCurrency(state.summaryStats.paidYTD)
    else return null
  },
  [MIDAS_STATUS_GROUPS](state) {
    if (state.midasStatGroups)
      return state.midasStatGroups.map((g) => {
        return {
          arg: g.status,
          val: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
        }
      })
    else return []
  },
  [PAID_BY_YEAR_GROUPS](state) {
    if (state.yearGroups)
      return state.yearGroups.map((g) => {
        return {
          arg: g.yearPaid.toString(),
          val: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
        }
      })
    else return []
  },
  [CLIENT_STATUS_GROUPS](state) {
    if (state.clientFacingStatGroups)
      return state.clientFacingStatGroups.map((g) => {
        return {
          arg: g.status,
          val: g.pct,
          netReclaim: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
          key: g.status,
        }
      })
    else return []
  },
  [COUNTRY_GROUPS](state) {
    if (state.countryGroups)
      return state.countryGroups.map((g) => {
        return {
          arg: g.coiName,
          val: g.pct,
          netReclaim: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
          key: g.status,
        }
      })
    else return []
  },
  [FI_ACCT_GROUPS](state) {
    if (state.fiAcctGroups)
      return state.fiAcctGroups.map((g) => {
        return {
          acct: g.acctCode,
          inst: g.fiCode,
          fiName: g.fiName,
          bo: g.boName,
          netRclm: g.netReclaim,
          numTransactions: g.transCount,
        }
      })
    else return []
  },
}

const mutations = {
  [SET_VIS_DATA](state, payload) {
    state.summaryStats = payload.summaryStats
    state.midasStatGroups = payload.midasStatGroups
    state.clientFacingStatGroups = payload.clientFacingStatGroups
    state.yearGroups = payload.yearGroups
    state.countryGroups = payload.countryGroups
    state.clientFacingStatGroups = payload.clientFacingStatGroups
    state.fiAcctGroups = payload.fiAcctGroups
  },
  [SET_LF_API_URL](state, payload) {
    state.lfApiUrl = payload
  },
  [SET_VIS_DATA_LOADED](state, payload) {
    state.visDataLoaded = payload
  },
  [SET_VIS_ERROR_OCCURED](state, payload) {
    state.visErrorOccured = payload
  },
  [SET_NO_DATA_ERROR](state, payload) {
    state.noDataError = payload
  },
  [SET_REPORT_LOADING](state, payload) {
    state.reportLoading = payload
  },
  [SET_EXCEL_LOADING](state, payload) {
    state.excelLoading = payload
  },
  [SET_PDF_LOADING](state, payload) {
    state.pdfLoading = payload
  },
  [SET_REPORT_ERROR](state, payload) {
    state.reportError = payload
  },
  [SET_FILTER_STACK](state, { key, filterStack }) {
    state.filterStackDict[key] = filterStack
  },
}

const actions = {
  // Update the filter and get updated groups from the REST API
  [UPDATE_FILTER]({ commit, rootState }, filter) {
    // Appending the viewReportAs.userId from the vuex reports module enables
    // admin users to emulate other users/clients
    filter.viewAsUserId = rootState.reports.viewReportAs?.userId ?? 0

    commit(SET_VIS_DATA_LOADED, false)
    getDataGroups(
      filter,
      (data) => {
        if (data) commit(SET_VIS_DATA, data)
        commit(SET_VIS_DATA_LOADED, true)
        commit(SET_VIS_ERROR_OCCURED, null)
        commit(SET_NO_DATA_ERROR, checkForNoDataError(data))
      },
      (jqXHR) => {
        commit(SET_VIS_ERROR_OCCURED, jqXHR)
        commit(SET_VIS_DATA_LOADED, true)
        commit(SET_NO_DATA_ERROR, false)
      },
    )
  },
  [DOWNLOAD_EXCEL_FILE]({ commit, state, rootState }, filter) {
    // Appending the viewReportAs.userId from the vuex reports module enables
    // admin users to emulate other users/clients
    filter.viewAsUserId = rootState.reports.viewReportAs?.userId ?? 0

    if (state[REPORT_LOADING] == false) {
      commit(SET_REPORT_LOADING, true)
      commit(SET_EXCEL_LOADING, true)
      getExcelData(
        filter,
        (data) => {
          let blob = b64toBlob(data.file_binary, data.file_extension)
          let filename = `${data.file_name}`
          saveAs(blob, filename)
          commit(SET_REPORT_LOADING, false)
          commit(SET_EXCEL_LOADING, false)
          commit(SET_REPORT_ERROR, null)
        },
        (jqXHR) => {
          commit(SET_REPORT_LOADING, false)
          commit(SET_EXCEL_LOADING, false)
          commit(SET_REPORT_ERROR, jqXHR)
        },
      )
    }
  },
  [DOWNLOAD_DASHBOARD_PDF]({ commit, state, rootState }, filter) {
    // Appending the viewReportAs.userId from the vuex reports module enables
    // admin users to emulate other users/clients
    filter.viewAsUserId = rootState.reports.viewReportAs?.userId ?? 0

    if (state[REPORT_LOADING] == false) {
      commit(SET_REPORT_LOADING, true)
      commit(SET_PDF_LOADING, true)
      GetReclaimDashboardPdf(
        filter,
        (data) => {
          let blob = b64toBlob(data.binary, data.extension)
          let filename = `${data.name}${data.extension}`
          saveAs(blob, filename)
          commit(SET_REPORT_LOADING, false)
          commit(SET_PDF_LOADING, false)
          commit(SET_REPORT_ERROR, null)
        },
        (jqXHR) => {
          commit(SET_REPORT_LOADING, false)
          commit(SET_PDF_LOADING, false)
          commit(SET_REPORT_ERROR, jqXHR)
        },
      )
    }
  },
  [ACKNOWLEDGE_ERRORS]({ commit }) {
    commit(SET_VIS_ERROR_OCCURED, null)
    commit(SET_NO_DATA_ERROR, false)
    commit(SET_REPORT_ERROR, null)
  },
}

const contentTypes = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.pdf': 'application/pdf',
}

// helper methods
const b64toBlob = (b64Data, fileExtension = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentTypes[fileExtension] })
}

const checkForNoDataError = (data) => {
  if (!(data && data.fiAcctGroups && data.fiAcctGroups.length)) return true
  else return false
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
